import { createContext, useContext, type ReactNode } from "react";

/**
 * Plugin tRPC client context (DECOUPLE-04 / D-04).
 *
 * A plugin client surface obtains the typed tRPC client from this context —
 * provided by the host app — instead of importing `client/src/core/api`. This is
 * symmetric with `useAuth`/`FeatureGate`: the consumer half lives in
 * `@webtemplate/ui`, the concrete provider (with the real typed client) lives in
 * the app (Plan 06), where the `as any` cast also lives.
 *
 * `PluginTrpcClient` is deliberately OPAQUE/loose — the same permissive surface
 * plugins call `trpc.plugins.<id>.*` on today via the `coreTrpc as any` shim.
 * The app-side `AppRouter` type is kept OUT of this package (Pitfall 3): pulling
 * it in would couple every plugin's UI to the app's full router type and let a
 * plugin reach app internals via types. The app supplies the typed client; the
 * cast stays app-side.
 *
 * `PluginTrpcProvider` is ALSO the shared test render provider plugin client
 * tests use (Plan 06): a component calling `usePluginTrpc()` does not throw in
 * tests when wrapped with a fake client value. Kept dependency-free and trivially
 * mountable.
 */

/**
 * The opaque plugin tRPC client. Permissive by design — the concrete typed
 * client is supplied app-side. `unknown`-keyed so plugins access
 * `trpc.plugins.<id>.*` without this package knowing the app router shape.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PluginTrpcClient = any;

/** Null until a PluginTrpcProvider (in the app) mounts above the tree. */
export const PluginTrpcContext = createContext<PluginTrpcClient | null>(null);

/** Read the plugin tRPC client. Throws if used outside a PluginTrpcProvider. */
export function usePluginTrpc(): PluginTrpcClient {
  const client = useContext(PluginTrpcContext);
  if (!client) {
    throw new Error("usePluginTrpc must be used within a PluginTrpcProvider");
  }
  return client;
}

interface PluginTrpcProviderProps {
  value: PluginTrpcClient;
  children: ReactNode;
}

/**
 * Provides the plugin tRPC client to the subtree. The app mounts this with the
 * concrete typed client; tests mount it with a fake client value.
 */
export function PluginTrpcProvider({
  value,
  children,
}: PluginTrpcProviderProps) {
  return (
    <PluginTrpcContext.Provider value={value}>
      {children}
    </PluginTrpcContext.Provider>
  );
}
